import { AllSelection, EditorState } from "prosemirror-state";
import { baseKeymap } from "prosemirror-commands";
import {
	DOMSerializer,
	DOMParser as ProsemirrorParser,
	Schema,
} from "prosemirror-model";
import { getResult, isFunction } from "../../core/helpers/functional-util.js";
import { isString } from "../../core/helpers/string-util.js";
import { keymap } from "prosemirror-keymap";
import { marks, nodes } from "prosemirror-schema-basic";
import ActionsManager from "./actions/ActionsManager.js";

/**
 * Default AST provided to the editor when it's empty (the default state).
 *
 * It has to be non-empty, so that we can have our placeholder, thus the
 * injected paragraph.
 *
 * The selection must be valid, empty, but within the document.
 *
 * We used to have `0` as selection index. The problem with this, is that since
 * it's out of the selection range, it contains no node. Because of that, we
 * couldn't select a mark before typing text (i.e. I click "bold" before
 * starting to type).
 *
 * By using `1`, the selection is still empty (as the anchor and the head are
 * the same), but we now are within a valid range of node. Thus we can now
 * select a mark before typing.
 */
const defaultAST = {
	doc: { type: "doc", content: [{ type: "paragraph" }] },
	selection: { type: "text", anchor: 1, head: 1 },
};

/**
 * Manage the state of a Wysiwyg editor.
 * This class is responsible for providing a ProseMirror EditorState to the EditorView,
 * and to alter the editor content.
 *
 * @param {function|string|ProseMirror.JSON} content - Initial content of the editor
 * @param {AbstractAction[]} [actions] - List of actions to append to the editor
 * @param {function} onChange - Function trigger when the editor content changes
 * @param {boolean} forceHTML - When true, an HTML string is triggered, no matter what
 * @param {function} [dropdownMenuItemBuilder] - Takes the dropdown
 *   options as argument (it must contain an `items` and a `label`
 *   fields). It should return an instance of a dropdown compatible
 *   with ProseMirror menu items. Every dropdown of ProseMirror's
 *   MenuBar will use the same builder. Defaults to
 *   `defaultDropdownItemBuilder` but can be used with
 *   `wrapAsProseMirrorDropdown` to use one of our own widgets
 */
export default class WysiwygStateManager {
	constructor({
		actions,
		content,
		onChange,
		forceHTML,
		dropdownMenuItemBuilder,
	}) {
		this._actionsManager = new ActionsManager({
			actions,
			stateManager: this,
			dropdownMenuItemBuilder,
		});

		this._content = content;
		this._onChange = onChange;
		this._forceHTML = forceHTML;

		const initialContent = getResult(content);
		if (
			forceHTML ||
			this._shouldHandleContentAsHTMLString(initialContent)
		) {
			this._shouldHandleContentAsHTML = true;
		}
	}

	getEditorViewSpec() {
		const that = this;
		return {
			state: this._toEditorState(),
			nodeViews: this._getNodeViews(),
			dispatchTransaction: function (transaction) {
				const editorView = this;

				that._handleTransaction({
					editorView,
					onChange: that._onChange,
					transaction,
					forceHTML: that._forceHTML,
				});
			},
		};
	}

	getUpdateContentTransaction() {
		if (!isFunction(this._content)) {
			return null;
		}

		const newContent = this._content();

		return this._shouldHandleContentAsHTMLString(newContent)
			? this.replaceWholeTextWithHTML(newContent)
			: this.replaceWholeTextWithASTNode(newContent);
	}

	replaceSelectionWithHTML(html) {
		const astNode = this._makeDomContent({
			html,
		});

		return this.replaceSelectionWithASTNode(astNode);
	}

	replaceSelectionWithASTNode(node) {
		return (state, dispatch) => {
			if (!state) {
				return false;
			}

			// Avoid adding a new paragraph if the editor already contains an empty one
			if (this._isStateEmpty(state)) {
				return this.replaceWholeTextWithASTNode(node)(state, dispatch);
			}

			dispatch && dispatch(state.tr.replaceSelectionWith(node));
			return true;
		};
	}

	replaceSelectionWithInputValue(nodeSpec) {
		return (state, dispatch) => {
			if (!state) {
				return false;
			}

			if (nodeSpec.type === "html") {
				return this.replaceSelectionWithHTML(nodeSpec.content)(
					state,
					dispatch,
				);
			}

			const builder = this._schema.nodes[nodeSpec.type];
			const node = builder.create(nodeSpec.attributes);

			return this.replaceSelectionWithASTNode(node)(state, dispatch);
		};
	}

	replaceWholeTextWithHTML(html) {
		const astNode = this._makeDomContent({
			html,
		});

		return this.replaceWholeTextWithASTNode(astNode);
	}

	replaceWholeTextWithASTNode(node) {
		return (state, dispatch) => {
			if (!state) {
				return false;
			}

			const selection = new AllSelection(state.doc);
			const transaction = state.tr;

			selection.replaceWith(transaction, node);
			dispatch && dispatch(transaction);

			return true;
		};
	}

	_isStateEmpty(state) {
		// When empty, the editor state contains an empty paragraph
		return state.doc.content.toString() === "<paragraph>";
	}

	_getSchema() {
		if (this._schema) {
			return this._schema;
		}

		const sanitizedBasicSchema = this._sanitizedBasicSchema();

		this._schema = new Schema({
			nodes: sanitizedBasicSchema.spec.nodes.append(
				this._actionsManager.getNodes(),
			),
			marks: sanitizedBasicSchema.spec.marks.append(
				this._actionsManager.getMarks(),
			),
		});

		return this._schema;
	}

	/**
	 * Some default nodes aren't supported, so we need to remove them from the schema.
	 * For now, the only unsupported node is `image` as we do not support inline
	 * image upload.
	 *
	 * @see {@link https://3.basecamp.com/4201305/buckets/26687532/todos/4759449654}
	 * @return {ProseMirror.Schema}
	 */
	_sanitizedBasicSchema() {
		const schema = new Schema({ nodes, marks });

		schema.spec.nodes = schema.spec.nodes.remove("image");
		schema.spec.marks = schema.spec.marks.remove("link");

		return schema;
	}

	_getNodeViews() {
		return this._actionsManager.getNodeViews();
	}

	_getPlugins() {
		if (this._plugins) {
			return this._plugins;
		}

		const plugins = this._actionsManager.getPlugins({
			schema: this._getSchema(),
		});

		plugins.push(...this._getDefaultPlugins());
		this._plugins = plugins;
		return plugins;
	}

	_getDefaultPlugins() {
		return [keymap(baseKeymap)];
	}

	_toEditorState() {
		if (this._shouldHandleContentAsHTML) {
			return EditorState.create({
				doc: this._getDocField(), // the doc contains the schema
				plugins: this._getPlugins(),
			});
		}

		const resolvedContent = getResult(this._content);
		return EditorState.fromJSON(
			{
				schema: this._getSchema(),
				plugins: this._getPlugins(),
			},
			resolvedContent || defaultAST, // we don't use argument default here because we want to handle `""` as undefined
		);
	}

	_makeDomContent({ html }) {
		const content = new DOMParser().parseFromString(html, "text/html");
		return ProsemirrorParser.fromSchema(this._getSchema()).parse(content);
	}

	/**
	 * Handle a transaction (a change of state) in the editor. This is used to hooked on content changes.
	 *
	 * @param {ProseMirror.EditorView} editorView - View triggering the new transaction
	 * @param {function} onChange - Function trigger when the editor content changes
	 * @param {ProseMirror.Transaction} transaction - The change that was
	 * operated in the prosemirror editor that triggered the
	 * dispatchTransaction method
	 * @param {boolean} forceHTML - When true, an HTML string is triggered, no matter what
	 * @private
	 */
	_handleTransaction({ editorView, onChange, transaction, forceHTML }) {
		this._dispatchTransaction({ editorView, transaction });
		this._triggerOnChange({ editorView, transaction, onChange, forceHTML });
	}

	/**
	 * Original function updating the prosemirror editor viewer that
	 * has to be called when modifying the original
	 * dispatchTransaction method.  Unfortunately, modifying this
	 * method is the only way to add a callback to a change happening
	 * in the editor. This code comes from the prosemirror code and
	 * mustn't be altered.
	 *
	 * @param {ProseMirror.EditorView} editorView - View triggering the new transaction
	 * @param {ProseMirror.Transaction} transaction - The change that was
	 * operated in the prosemirror editor that triggered the
	 * dispatchTransaction method
	 */
	_dispatchTransaction({ editorView, transaction }) {
		if (!editorView) {
			return;
		}
		const newState = editorView.state.apply(transaction);
		editorView.updateState(newState);
	}

	/**
	 * Calls the _onChange function with the editor content as
	 * argument if the prosemirror transaction is related to a change
	 * in the document.
	 *
	 * @param {ProseMirror.EditorView} editorView - View triggering the new transaction
	 * @param {ProseMirror.Transaction} transaction - The change that was
	 * operated in the prosemirror editor that triggered the
	 * dispatchTransaction method
	 * @param {function} onChange - Function triggered when a change occurs
	 * @param {boolean} forceHTML - When true, an HTML string is triggered, no matter what
	 */
	_triggerOnChange({ editorView, transaction, onChange, forceHTML }) {
		if (!transaction.docChanged) {
			return;
		}

		if (this._shouldHandleContentAsHTML || forceHTML) {
			onChange(this._getHtml({ editorView }));
			return;
		}

		onChange(editorView.state.toJSON());
	}

	/**
	 * Retrieve the editor content as an HTML string.
	 *
	 * @param {ProseMirror.EditorView} editorView - View triggering the new transaction
	 * @return {string}
	 */
	_getHtml({ editorView }) {
		if (!editorView) {
			return "";
		}

		const fragment = DOMSerializer.fromSchema(
			editorView.state.schema,
		).serializeFragment(editorView.state.doc.content);
		const div = document.createElement("div");
		div.appendChild(fragment);
		return div.innerHTML;
	}

	_shouldHandleContentAsHTMLString(content) {
		return content && isString(content);
	}

	_getDocField() {
		const html = getResult(this._content) || "";
		const content = new DOMParser().parseFromString(html, "text/html");

		return ProsemirrorParser.fromSchema(this._getSchema()).parse(content);
	}
}
